import React, { useMemo } from 'react'
import { NavLink, useRouteMatch, match, NavLinkProps } from 'react-router-dom'
import { versionedRoutes } from '@sketch/modules-common'

import {
  useCompareSearch,
  useComparedShortId,
} from '../../compare/useCompareToggle'

const versionRouteArray = Object.values(versionedRoutes)

type NavLinkPassedProps = OmitSafe<NavLinkProps, 'to'>

interface VersionLinkProps extends NavLinkPassedProps {
  isLatestVersion: boolean
  versionShortId: string
  /**
   * Carry a running comparison onto the link. Off for a link that is copied
   * rather than followed: it names a version, not the reader's comparison.
   */
  keepCompare?: boolean
}

export interface PreventiveVersionLinkProps extends NavLinkPassedProps {
  isLatestVersion: boolean
  versionShortId?: string | false
  keepCompare?: boolean
}

const createLink = (
  route: (typeof versionRouteArray)[number],
  params: match['params'],
  versionShortId: string,
  isLatestVersion: boolean
) => {
  const { LATEST, VERSION } = route
  const routeParams = params as any

  return isLatestVersion
    ? LATEST.create(routeParams)
    : VERSION?.create({
        ...routeParams,
        versionShortId,
      })
}

const VersionLink: React.FCC<VersionLinkProps> = props => {
  const {
    children,
    versionShortId,
    isLatestVersion,
    keepCompare = true,
    ...linkProps
  } = props
  const route = useRouteMatch()
  const compareSearch = useCompareSearch()
  const { comparedShortId } = useComparedShortId()

  // Opening the compared version would leave it compared with itself: nothing
  // to show, and no row left offering to stop it.
  const search =
    keepCompare && versionShortId !== comparedShortId ? compareSearch : ''

  const versionedRoute =
    useMemo(
      () =>
        versionRouteArray.find(({ LATEST, VERSION }) => {
          return [LATEST.template(), VERSION.template()].includes(route.path)
        }),
      [route.path]
    ) || versionedRoutes.SHARE_VIEW

  const to = createLink(
    versionedRoute,
    route.params,
    versionShortId,
    isLatestVersion
  )

  return (
    <NavLink
      data-testid="version-item-link"
      to={{ pathname: to, search }}
      exact
      {...linkProps}
    >
      {children}
    </NavLink>
  )
}

const PreventiveVersionLink: React.FCC<PreventiveVersionLinkProps> = props => {
  if (props.versionShortId) {
    return <VersionLink {...props} versionShortId={props.versionShortId} />
  }

  return <>{props.children}</>
}

export default PreventiveVersionLink
